import { useState, useEffect, useCallback, useMemo } from 'react';
import { RSVPResponse, RSVPStats } from '../../../types/invitation';
import { rsvpService, seatingService } from '../../../lib/supabase';
import { createLogger } from '../../../lib/logger';
import { RSVPResponseWithTable, PhysicalInvitationStats, SeatAssignmentStats } from '../types';

const log = createLogger('rsvp');

function mergeRSVPWithSeating(responses: RSVPResponse[], seating: any): RSVPResponseWithTable[] {
  let tables: any[] = [];
  if (seating) {
    if (Array.isArray(seating)) {
      tables = seating;
    } else if (seating.tables && Array.isArray(seating.tables)) {
      tables = seating.tables;
    }
  }

  if (tables.length === 0) {
    return responses.map(response => ({ ...response, tableName: '未分配', tableId: undefined }));
  }

  const rsvpToTableMap = new Map<string, { tableName: string; tableId: number }>();

  tables.forEach((table: any) => {
    if (table.guests && Array.isArray(table.guests)) {
      table.guests.forEach((guest: any) => {
        const rsvpId = typeof guest === 'string' ? guest : guest.id;
        if (rsvpId) {
          rsvpToTableMap.set(rsvpId, {
            tableName: table.name || `桌次 ${table.id}`,
            tableId: table.id
          });
        }
      });
    }
  });

  return responses.map(response => {
    const tableInfo = rsvpToTableMap.get(response.id);
    return {
      ...response,
      tableName: tableInfo?.tableName || '未分配',
      tableId: tableInfo?.tableId
    };
  });
}

const INITIAL_STATS: RSVPStats = {
  total: 0,
  attending: 0,
  declined: 0,
  totalGuests: 0,
  guestSideStats: {
    bride: { count: 0, totalGuests: 0, percentage: 0 },
    groom: { count: 0, totalGuests: 0, percentage: 0 }
  }
};

const INITIAL_PHYSICAL_STATS: PhysicalInvitationStats = {
  totalRequests: 0,
  totalGuests: 0,
  addressList: []
};

export function useRSVPData(inviteCode: string, userId: string | undefined, refreshTrigger?: number) {
  const [rsvpResponses, setRSVPResponses] = useState<RSVPResponseWithTable[]>([]);
  const [rsvpStats, setRSVPStats] = useState<RSVPStats>(INITIAL_STATS);
  const [physicalInvitationStats, setPhysicalInvitationStats] = useState<PhysicalInvitationStats>(INITIAL_PHYSICAL_STATS);
  const [loading, setLoading] = useState(true);
  const [seatingData, setSeatingData] = useState<any>(null);

  const loadData = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const [responses, stats, seating, physicalStats] = await Promise.all([
        rsvpService.getRSVPsByInviteCode(inviteCode, userId),
        rsvpService.getRSVPStats(inviteCode, userId),
        seatingService.loadSeatingArrangement(inviteCode).catch(() => null),
        rsvpService.getPhysicalInvitationStats(inviteCode, userId)
      ]);

      const responsesWithTable = mergeRSVPWithSeating(responses, seating);

      setRSVPResponses(responsesWithTable);
      setRSVPStats(stats);
      setSeatingData(seating);
      setPhysicalInvitationStats(physicalStats);
    } catch (error) {
      log.error('Error loading RSVP data:', error);
    } finally {
      setLoading(false);
    }
  }, [inviteCode, userId]);

  useEffect(() => {
    if (inviteCode) {
      loadData();
    }
  }, [inviteCode, refreshTrigger, loadData]);

  const attendees = useMemo(
    () => rsvpResponses.filter(r => r.rsvpData.attending === 'yes'),
    [rsvpResponses]
  );

  const declinedResponses = useMemo(
    () => rsvpResponses.filter(r => r.rsvpData.attending === 'no'),
    [rsvpResponses]
  );

  const responsesWithMessages = useMemo(
    () => rsvpResponses.filter(r => r.rsvpData.message || r.rsvpData.specialRequests),
    [rsvpResponses]
  );

  const seatAssignmentStats = useMemo((): SeatAssignmentStats => {
    const assignedCount = attendees.filter(r => r.tableId).length;
    return {
      totalAttending: attendees.length,
      assignedSeats: assignedCount,
      unassignedSeats: attendees.length - assignedCount,
      assignmentRate: attendees.length > 0 ? Math.round((assignedCount / attendees.length) * 100) : 0
    };
  }, [attendees]);

  const confirmationRate = useMemo(
    () => rsvpStats.total > 0 ? Math.round(rsvpStats.attending / rsvpStats.total * 100) : 0,
    [rsvpStats]
  );

  const dietaryStats = useMemo(() => {
    return attendees.reduce((acc, attendee) => {
      if (attendee.rsvpData.dietaryRestrictions) {
        attendee.rsvpData.dietaryRestrictions.forEach(restriction => {
          const key = restriction.id === 'vegetarian' ? '素食' : '兒童椅';
          acc[key] = (acc[key] || 0) + restriction.count;
        });
      }
      return acc;
    }, {} as Record<string, number>);
  }, [attendees]);

  return {
    rsvpResponses,
    rsvpStats,
    physicalInvitationStats,
    loading,
    seatingData,
    attendees,
    declinedResponses,
    responsesWithMessages,
    seatAssignmentStats,
    confirmationRate,
    dietaryStats,
    refresh: loadData,
    setAttendees: setRSVPResponses,
  };
}
